import { Link } from 'react-router-dom';
import PageHeader from '../components/Shared/PageHeader';
import { c } from '../theme';

// ── The calculation dictionary ───────────────────────────────────────────────
//
// One definition per number on the Overtime board, published with the board
// because a definition kept somewhere else is a definition nobody reads.
//
// It exists for a specific job. The enhancement brief's disposition for the
// standalone indirect-overtime dashboard is "reconcile the migrated numbers
// against the current file for one full period before switching off". When the
// portal says one figure and the finance spreadsheet says another, this page is
// what turns that into a diff instead of an argument -- and the standalone
// files cannot be retired until that reconciliation passes.
//
// The last section is the important one. It states what this board CANNOT
// answer and why, because every one of those gaps is a question somebody will
// otherwise ask for repeatedly, and because a reader who knows the board holds
// no straight-time hours stops reading "Overtime Cost" as "labor cost".
//
// Definitions are written as rules rather than counts so they do not rot: "the
// pay periods that ended in the month", not "four or five periods".

const SECTIONS = [
  {
    title: 'Measures',
    intro: 'Every figure below counts only rows from committed payroll batches. A batch being previewed or cancelled contributes nothing.',
    items: [
      {
        term: 'Overtime Cost',
        def: 'The sum of the AMOUNT column on payroll rows classified as overtime.',
        why: 'This is premium pay, not total labor cost. Every income code the extract has ever carried is a premium code — overtime, double time, shift differentials and missed meals. No straight-time row has ever been received, so this figure cannot be read as what the work cost.',
      },
      {
        term: 'Overtime Hours',
        def: 'The sum of the HOURS column on the same rows.',
      },
      {
        term: 'Classified as overtime',
        def: 'A row counts when any one of three signals fires: the words "overtime" or "double time" in the income description, a standalone OT or DT token in that description, or an OT/DT fragment in the income code itself.',
        why: 'The rule lives in one file so it can be corrected in one place. Rows that fail it are still stored — the classification is a flag, not a filter — so if Workday ever sends straight time, it lands without a schema change.',
      },
      {
        term: 'Employees w/ OT',
        def: 'Distinct employee numbers across the whole selected span.',
        why: 'Someone with overtime in six of six periods counts once, not six times. Summing this across periods double-counts.',
      },
      {
        term: 'Cost Centers',
        def: 'Distinct cost centers carrying overtime in the span — not the number that exist.',
      },
      {
        term: 'Avg Hrs / Employee',
        def: 'Overtime hours divided by distinct employees across the span.',
        why: 'Hours per person for the whole span, not hours per person per period.',
      },
      {
        term: 'The change figure under each tile',
        def: 'A single pay period is compared with the most recent earlier period that carried at least five employees, or ten per cent of the current period’s employee count, whichever is larger. A multi-period span is compared with the equally long span immediately before it.',
        why: 'Payroll issues small correction runs covering one or two people. Comparing a full week against one of those produces a percentage that looks like a collapse and means nothing, so those runs are skipped as a comparison basis.',
      },
    ],
  },
  {
    title: 'Role',
    intro: 'The dimension below cost center: which role the overtime is in, and whether it is direct or indirect labor.',
    items: [
      {
        term: 'Job profile, family, group',
        def: 'The payroll job profile, mapped to a job family and one of three family groups through the HR Job Profile Classification Bible.',
        why: 'The join is on a normalized key — trimmed, internal whitespace collapsed, lowercased — because payroll spellings and the bible do not match byte for byte.',
      },
      {
        term: 'Labor category',
        def: 'HR\u2019s own four-way classification of each job profile: Direct, Indirect, G&A or Sales labor. Assigned per profile, not per family.',
        why: 'Per-family assignment cannot work here \u2014 12 of the 42 families carry more than one category, and Post Collections Operations carries all four. Leads, fuelers and yardsmen are indirect inside otherwise direct families.',
      },
      {
        term: 'Labor class (direct / indirect)',
        def: 'The two-way rollup of the category above: direct is Direct Labor, indirect is everything else.',
        why: 'Kept because it is what the Indirect lens filters on. It deliberately folds G&A and Sales into indirect, so use Labor category when those need to be separate \u2014 which is what the indirect-and-SG&A view asks for.',
      },
      {
        term: 'Applied as at the week reported',
        def: 'A row is classified as it stood during the week being reported, not as it stands today.',
        why: 'Reclassifying opens a new dated row rather than editing the old one, so a week already shown to a board keeps the class it was shown under. The classification is authoritative but has not yet been released for publication by Operations and Finance.',
      },
      {
        term: 'Not in the mapping',
        def: 'Payroll job profiles with no entry in the bible. Their hours and dollars stay in every unfiltered total and are reported as their own row.',
        why: 'They are held out of the rankings because they are not a job family, and never dropped because a total that quietly shrinks reads as less overtime rather than as a gap in the mapping.',
      },
      {
        term: 'Filtered totals',
        def: 'Any cut by role, family, group or class excludes rows with no mapping, because an unmapped row has no class to match.',
        why: 'A filtered total is therefore legitimately smaller than the unfiltered board. The two are not meant to agree.',
      },
    ],
  },
  {
    title: 'Location',
    items: [
      {
        term: 'Cost center',
        def: 'The unit payroll reports against. At Athens this is both a yard and a department — Finance and Paramount sit in the same list.',
        why: 'They are siblings distinguished by type, not levels of a hierarchy. A yard does not contain a department.',
      },
      {
        term: 'Division',
        def: 'A grouping above cost center, drafted from the cost center numbering blocks and names.',
        why: 'Drafted, not authoritative. Several assignments are flagged as doubtful and await review by Operations. One to watch: Cash Applications/Collections is receivables collections, not waste collections.',
      },
      {
        term: '(unassigned)',
        def: 'A cost center appearing in payroll with no entry in the reference table.',
        why: 'Shown as its own bucket rather than folded into a real division, so a gap in the reference data cannot be mistaken for a real total.',
      },
      {
        term: 'Lens',
        def: 'A saved filter on this board, not a separate dashboard. Indirect is the labor class; Fleet maintenance is the maintenance family group.',
      },
    ],
  },
  {
    title: 'Time',
    items: [
      {
        term: 'Pay period',
        def: 'One weekly payroll run, identified by the date it ended.',
      },
      {
        term: 'A month',
        def: 'The pay periods that ENDED in that month.',
        why: 'Payroll runs weekly and routinely straddles a month boundary, so a month is a whole number of payroll runs and will not match a calendar-month figure from another system. The header states the exact span being totalled.',
      },
      {
        term: 'Level',
        def: 'L0 reads months and asks whether the month is where it was committed to be. L1 reads pay periods and asks whether the week was made.',
        why: 'The level sets the grain deliberately. Reading a single payroll run against a monthly commitment is the comparison this arrangement exists to prevent.',
      },
    ],
  },
  {
    title: 'Who sees what',
    items: [
      {
        term: 'Cost center access',
        def: 'Administrators and finance reviewers see every cost center. Everyone else sees only the cost centers assigned to them, and every figure on the page is already scoped to those.',
        why: 'A restricted viewer is shown how many cost centers they are assigned against how many are active, because a total that silently omits a cost center looks exactly like a total that does not.',
      },
      {
        term: 'Employee-level detail',
        def: 'Named employee rows are hidden from read-only accounts.',
      },
    ],
  },
];

const GAPS = [
  ['OT as a percentage of hours',
   'The denominator is total hours worked, and the portal receives only premium rows. It cannot be derived or estimated from what is stored.'],
  ['Premium separated from base pay',
   'Pay rate is deliberately never read, mapped or stored by this module. The split cannot be computed even for the rows that are held.'],
  ['Variance against a target or budget',
   'No overtime targets exist anywhere in the schema. The per-yard reduction targets agreed with Operations have never been loaded.'],
  ['Comparison against the prior year',
   'History begins with the first payroll period loaded into the portal. Anything earlier has not been backfilled.'],
  ['Week-to-date pacing, and who is about to breach',
   'Payroll closes after the week ends. Answering "will we make this week" needs a daily time feed, which is a different Workday extract to the one this module receives.'],
  ['Overtime expressed as headcount, and the hire-versus-overtime break-even',
   'Needs a fringe rate, a loaded cost per hire and a target utilization figure. None are held in the portal.'],
];

export default function OvertimeDefinitions() {
  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Overtime"
        title="Calculation dictionary"
        subtitle="What each number on the Overtime board counts, and what the board cannot answer."
        actions={<Link to="/overtime" style={s.back}>Back to the board</Link>}
        alignActions="end"
      />

      <div style={s.lede}>
        Written so the board can be reconciled against the reports it is meant to
        replace. Where a figure here disagrees with another system, the definition
        is the place to look first — most disagreements are two systems counting
        different things rather than one of them being wrong.
      </div>

      {SECTIONS.map((section) => (
        <section key={section.title} style={s.section}>
          <h2 style={s.h2}>{section.title}</h2>
          {section.intro && <p style={s.intro}>{section.intro}</p>}
          <dl style={s.dl}>
            {section.items.map((item) => (
              <div key={item.term} style={s.row}>
                <dt style={s.dt}>{item.term}</dt>
                <dd style={s.dd}>
                  <div>{item.def}</div>
                  {item.why && <div style={s.why}>{item.why}</div>}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}

      <section style={s.section}>
        <h2 style={s.h2}>What this board cannot answer</h2>
        <p style={s.intro}>
          Each of these is a question the board is asked and cannot answer from the
          data it receives. None is a reporting gap that more querying would close;
          every one needs a source the portal does not have.
        </p>
        <dl style={s.dl}>
          {GAPS.map(([term, def]) => (
            <div key={term} style={s.row}>
              <dt style={{ ...s.dt, color: c.warningText }}>{term}</dt>
              <dd style={s.dd}>{def}</dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
}

const s = {
  page:    { maxWidth: '900px', margin: '0 auto' },
  back:    { fontSize: '13px', color: c.info, textDecoration: 'none' },
  lede:    { background: c.surfaceSunk, border: `1px solid ${c.border}`, borderRadius: '8px',
             padding: '12px 14px', fontSize: '13px', lineHeight: 1.6, color: c.textStrong,
             marginBottom: '26px' },
  section: { marginBottom: '30px' },
  h2:      { fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
             color: c.textMuted, margin: '0 0 10px' },
  intro:   { fontSize: '13px', lineHeight: 1.6, color: c.textStrong, margin: '0 0 12px' },
  dl:      { margin: 0, background: c.surface, border: `1px solid ${c.border}`,
             borderRadius: '12px', overflow: 'hidden' },
  row:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
             gap: '4px 20px', padding: '14px 16px', borderBottom: `1px solid ${c.canvas}` },
  dt:      { fontSize: '13px', fontWeight: 600, color: c.text },
  dd:      { margin: 0, fontSize: '13px', lineHeight: 1.6, color: c.textStrong },
  why:     { marginTop: '6px', fontSize: '12px', lineHeight: 1.55, color: c.textMuted },
};
